import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { NakedUl } from '@frontend/design-system';
import { useGetEntityArchiveStatus } from '../../../hooks';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../../stores/schedule-appointment-info-panel.store';
import { ExtendedAppointment } from '../../../types';
import { checkIsNonAttendedEventStatus } from '../../calendar-view/utils';
import { AppointmentListItem } from '../appointment-list-item';
import { AppointmentFloatingActions } from './appointment-floating-actions';

interface Props {
  appointments: ExtendedAppointment[];
}

export const AppointmentList = ({ appointments }: Props) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const { setSelectedAppointment } = useScheduleAppointmentInfoPanelShallowStore('setSelectedAppointment');
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const handlePersonClick = (appointment: ExtendedAppointment) => {
    const personId = appointment.person?.personId;
    const locationId = appointment.locationId;

    if (personId && locationId) {
      setPersonId(personId, true, locationId);
    }
  };

  return (
    <NakedUl css={listStyle}>
      {appointments.map((appointment) => (
        <AppointmentItem
          key={appointment.id}
          appointment={appointment}
          isSchedulerV3FlagEnabled={isScheduleV3FlagEnabled}
          onPersonClick={() => handlePersonClick(appointment)}
          onItemClick={() => setSelectedAppointment(appointment)}
        />
      ))}
    </NakedUl>
  );
};

interface AppointmentItemProps {
  appointment: ExtendedAppointment;
  isSchedulerV3FlagEnabled?: boolean;
  onPersonClick: () => void;
  onItemClick: () => void;
}

const AppointmentItem = ({
  appointment,
  isSchedulerV3FlagEnabled,
  onPersonClick,
  onItemClick,
}: AppointmentItemProps) => {
  const locationId = appointment.locationId || '';
  const personId = appointment.person?.personId || '';

  const { data: personData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !!personId,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );
  const { isAppointmentTypeArchived, isProviderArchived } = useGetEntityArchiveStatus({
    appointmentTypeId: appointment?.appointmentTypeId || '',
    providerId: appointment?.providerIds?.[0] || '',
    locationId,
  });
  const isNonAttendedV3Event =
    isSchedulerV3FlagEnabled && appointment?.statusOfficeView
      ? checkIsNonAttendedEventStatus(appointment.statusOfficeView as EventStatus)
      : false;

  if (!locationId || !personId) return null;

  return (
    <li key={appointment.id} css={itemContainerStyle} onClick={onItemClick}>
      <AppointmentFloatingActions locationId={locationId} person={personData}>
        <AppointmentListItem
          appointment={appointment}
          onPersonClick={onPersonClick}
          isApptTypeArchived={isAppointmentTypeArchived}
          isProviderArchived={isProviderArchived}
          shouldShowStrikethrough={isNonAttendedV3Event}
        />
      </AppointmentFloatingActions>
    </li>
  );
};

const listStyle = css({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  background: theme.colors.neutral5,
});

const itemContainerStyle = css({
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  backgroundColor: theme.colors.white,
  transition: 'box-shadow 0.2s ease-in-out',
  ':hover': {
    boxShadow: theme.shadows.light,
    cursor: 'pointer',
  },
});
